import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Sender } from '@sendbird/chat/message';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { SendbirdChatApi } from 'src/app/shared/apis/sendbird-chat.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CsCurrencyConverterComponent } from 'src/app/shared/components/cs-currency-converter/cs-currency-converter.component';
import { CsCurrencyUnitDisplayComponent } from 'src/app/shared/components/cs-currency-unit-display/cs-currency-unit-display.component';
import { CsPopupService } from 'src/app/shared/components/cs-popup/cs-popup.service';
import { LoadingModule } from 'src/app/shared/directive/loading/loading.module';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { SendbirdChatService } from '../../sendbird-chat.service';

enum UserRole {
  Moderator = 'Moderator',
  User = 'User',
}

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    CommonModule,
    CSButtonComponent,
    LoadingModule,
    PipesModule,
    CsCurrencyConverterComponent,
    CsCurrencyUnitDisplayComponent,
  ],
  selector: 'app-chat-profile',
  templateUrl: './chat-profile.component.html',
  styleUrls: ['./chat-profile.component.scss'],
})
export class ChatProfileComponent implements OnInit {
  @Input() sender!: Sender;
  readonly UserRole = UserRole;
  role: UserRole = UserRole.User;
  avatarUrl: string = '';
  hiddenMode = false;
  isLoaded = false;
  isBlock = true;
  isSelfUser = false;
  loading = false;
  currentCurrency: string = 'USDT';
  profileData: {
    registerDate: string;
    highestMultipler: number;
    highestMultiplerBetId: string;
    totalWins: number;
    totalWagered: number;
    tipsSent: number;
    tipsReceived: number;
  } | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ChatProfileComponent>,
    private sendbirdChatApi: SendbirdChatApi,
    private sendbirdChatService: SendbirdChatService,
    private csPopupService: CsPopupService,
    public appService: AppService,
  ) {}

  async ngOnInit() {
    this.loading = true;
    const { metaData = {} } =
      this.sendbirdChatService.userList$.value.find(user => user.userId === this.data.sender.userId) || {};

    // Initialize or fetch data if needed
    this.sender = this.data.sender;
    this.avatarUrl = this.data.avatarUrl || '';
    this.isBlock = this.sendbirdChatService.checkBlockedUser(this.sender.userId);
    this.isSelfUser = this.sendbirdChatService.checkSelfUser(this.sender.userId);

    this.appService.currentCurrency$.pipe(untilDestroyed(this)).subscribe(v => {
      this.currentCurrency = v.currency;
    });

    if ((metaData as any)?.role === 'moderator') {
      this.role = UserRole.Moderator;
      this.isLoaded = true;
      this.loading = false;
    } else {
      try {
        const response = await firstValueFrom(this.sendbirdChatApi.getChatProfile(this.sender.userId));

        if (response?.data) {
          const { isInvisible, info } = response.data;

          this.hiddenMode = isInvisible && !this.isSelfUser;
          this.profileData = info;
          this.isLoaded = true;
        }
      } catch (error) {
        this.hiddenMode = true;
      } finally {
        this.loading = false;
      }
    }
  }

  handleCloseChatProfile() {
    this.dialogRef.close();
  }

  async handleClickBlockUser() {
    if (this.isBlock) {
      // Unblock user
      await this.sendbirdChatService.unblockUser(this.sender);
      this.isBlock = this.sendbirdChatService.checkBlockedUser(this.sender.userId);
    } else {
      this.csPopupService.info({
        header: 'SILENCE',
        title: `You are going to silence user @${this.sender.nickname}`,
        message:
          'Silencing them means you won’t be able to see their messages. To undo this, please go to your settings page.',
        confirmText: 'Silence this user',
        color: 'danger',
        textAlign: 'left',
        confirmCallback: async () => {
          // Block user
          await this.sendbirdChatService.blockUser(this.sender);
          this.isBlock = this.sendbirdChatService.checkBlockedUser(this.sender.userId);
        },
      });
    }
  }
  handleClickTip() {}

  handleClickHighestBet() {}
}
